import { GameId, SupportedLanguage } from '@common'
import { ListenerEffectAPI, PayloadAction } from '@reduxjs/toolkit'
import router from 'next/router'

import { api } from '@/api'
import { syncWithDraft } from '@/features/checkout/checkoutSlice'
import { setCurrentGame } from '@/features/common/commonSlice'
import { LOGIN_STATUS } from '@/features/user/userSlice'
import { AppDispatch, RootState } from '@/store'
import { compareCartRows } from '@/utils/compareCartRows'
import { normalizeCreatorBoostCode } from '@/utils/normalizeCreatorBoostCode'

const onStoreFrontChange = async (
  action: PayloadAction<GameId | undefined>,
  listenerAPI: ListenerEffectAPI<RootState, AppDispatch>,
) => {
  const { getState, dispatch } = listenerAPI

  const market = getState().config.market
  const isLoggedIn = getState().user.loginStatus === LOGIN_STATUS.LOGGED_IN
  const gameId = action.payload

  if (!market || !gameId || !isLoggedIn) {
    return
  }

  const clientLanguage = router.locale as SupportedLanguage
  const clientRows = getState().checkout.rows.filter((r) => r.product.game === gameId)
  const clientCreatorBoost = getState().checkout.creatorBoostCode.value
  const clientCountryCode = market.countryCode
  const existingDraft = await dispatch(api.endpoints.getDraft.initiate(gameId)).unwrap()

  if (!existingDraft) {
    const response = await dispatch(
      api.endpoints.updateDraft.initiate({
        countryCode: clientCountryCode,
        language: clientLanguage,
        rows: clientRows.map(({ product: { id: sku }, quantity }) => ({ sku, quantity })),
        creatorBoostCode: clientCreatorBoost ? clientCreatorBoost : undefined,
        gameId,
      }),
    ).unwrap()
    dispatch(syncWithDraft({ market, draft: response }))
    return
  }

  if (market.countryCode !== existingDraft.country) {
    return
  }

  if (clientLanguage !== existingDraft.language) {
    return
  }

  const updateRows = clientRows.length > 0 && compareCartRows(existingDraft.rows, clientRows) ? clientRows : false
  const normalizedClientCreatorBoost = normalizeCreatorBoostCode(clientCreatorBoost)
  const normalizedExistingCreatorBoost = normalizeCreatorBoostCode(existingDraft.creatorBoost)
  const updateBoostCode = normalizedClientCreatorBoost !== normalizedExistingCreatorBoost ? clientCreatorBoost : false
  const updateCountry = clientCountryCode !== existingDraft.country ? clientCountryCode : false
  const updateLanguage = clientLanguage !== existingDraft.language ? clientLanguage : false

  if (updateRows || updateBoostCode || updateCountry || updateLanguage) {
    const updatedDraftResponse = await dispatch(
      api.endpoints.updateDraft.initiate({
        countryCode: market.countryCode,
        language: updateLanguage ? updateLanguage : existingDraft.language,
        rows: updateRows ? updateRows.map(({ product: { id: sku }, quantity }) => ({ sku, quantity })) : undefined,
        creatorBoostCode: clientCreatorBoost ? clientCreatorBoost : undefined,
        gameId,
      }),
    )
    if ('error' in updatedDraftResponse) {
      const draftResponse = await dispatch(api.endpoints.getDraft.initiate(gameId)).unwrap()
      dispatch(syncWithDraft({ market, draft: draftResponse }))
    } else {
      dispatch(syncWithDraft({ market, draft: updatedDraftResponse.data }))
    }
  } else {
    dispatch(syncWithDraft({ market, draft: existingDraft }))
  }
}

const getDraftListenerMiddleware = {
  effect: onStoreFrontChange,
  matcher: setCurrentGame.match,
} as const

export default getDraftListenerMiddleware
